import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deliveryAPI, cityAPI, workAreaAPI, paymentAPI } from '../../services/api';
import { load } from '@cashfreepayments/cashfree-js';
import './DeliveryPartnerOnboarding.css';

const DeliveryPartnerOnboarding = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Available cities and work areas
  const [availableCities, setAvailableCities] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);

  // Form data state
  const [workDetails, setWorkDetails] = useState({
    workCity: '',
    workAreas: []
  });

  const [vehicleDetails, setVehicleDetails] = useState({
    vehicleType: '',
    vehicleNumber: ''
  });

  const [idDetails, setIdDetails] = useState({
    idType: '',
    aadharNumber: '',
    aadharLinkedMobile: '',
    panNumber: '',
    aadharDocument: null,
    panDocument: null,
    addressProofDocument: null
  });

  const [onboardingStatus, setOnboardingStatus] = useState('pending');

  // Free delivery partner slots state
  const [onboardingSettings, setOnboardingSettings] = useState({
    freeDeliveryPartnerLimit: 200,
    freeDeliverySlotsUsed: 0,
    remainingFreeSlots: 200,
    hasFreeSlotsAvailable: true
  });

  // Fetch available cities on mount
  useEffect(() => {
    fetchCities();
    checkOnboardingStatus();
    fetchOnboardingSettings();
  }, []);

  // Check for payment return from Cashfree
  useEffect(() => {
    const orderId = searchParams.get('order_id');
    if (orderId) {
      verifyPaymentAndSubmit();
    }
  }, [searchParams]);

  const fetchCities = async () => {
    try {
      const response = await cityAPI.getActiveCities();
      setAvailableCities(response.data.data.cities);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
    }
  };

  const fetchOnboardingSettings = async () => {
    try {
      const response = await deliveryAPI.getOnboardingSettings();
      setOnboardingSettings(response.data.data);
    } catch (err) {
      console.error('Failed to fetch onboarding settings:', err);
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      const response = await deliveryAPI.getOnboardingStatus();
      const status = response.data.data.onboardingStatus;
      setOnboardingStatus(status);

      // Load existing data if available
      const dp = response.data.data.deliveryPartner;
      if (dp) {
        if (dp.workCity) {
          setWorkDetails({
            workCity: dp.workCity,
            workAreas: dp.workAreas || []
          });
          fetchWorkAreas(dp.workCity);
        }
        if (dp.vehicleType) {
          setVehicleDetails({
            vehicleType: dp.vehicleType,
            vehicleNumber: dp.vehicleNumber || ''
          });
        }
        if (dp.idType) {
          setIdDetails(prev => ({
            ...prev,
            idType: dp.idType,
            aadharNumber: dp.aadharNumber || '',
            aadharLinkedMobile: dp.aadharLinkedMobile || '',
            panNumber: dp.panNumber || ''
          }));
        }
      }

      // Redirect if approved - go directly to dashboard
      if (status === 'approved') {
        console.log('✅ Delivery partner approved, redirecting to dashboard...');
        setTimeout(() => {
          navigate('/delivery/dashboard');
        }, 1000);
      } else if (status === 'under_review') {
        // Show status page for under_review
        setCurrentStep(8);
      }
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err);
    }
  };

  const verifyPaymentAndSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Check onboarding status to verify payment
      const response = await deliveryAPI.getOnboardingStatus();
      const dp = response.data.data.deliveryPartner;

      // Check if payment was successful
      if (dp && dp.onboardingFee && dp.onboardingFee.paid) {
        // Payment successful, submit for approval
        // Backend will verify payment and validate all fields from database
        await deliveryAPI.submitForApproval();

        // Clear URL parameters
        setSearchParams({});

        // Move to success step
        setCurrentStep(7);
      } else {
        setError('Payment verification pending. Please wait a moment and refresh the page.');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError(err.response?.data?.message || 'Failed to verify payment');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkAreas = async (city) => {
    try {
      const response = await workAreaAPI.getWorkAreasByCity(city);
      setWorkAreas(response.data.data.workAreas);
    } catch (err) {
      console.error('Failed to fetch work areas:', err);
      setWorkAreas([]);
    }
  };

  const handleCityChange = (city) => {
    setWorkDetails({
      workCity: city,
      workAreas: []
    });
    fetchWorkAreas(city);
  };

  const handleWorkAreaToggle = (areaId) => {
    setWorkDetails(prev => {
      const areas = prev.workAreas.includes(areaId)
        ? prev.workAreas.filter(a => a !== areaId)
        : [...prev.workAreas, areaId];
      return { ...prev, workAreas: areas };
    });
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size should not exceed 10MB');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPEG, PNG, and PDF files are allowed');
        return;
      }

      setIdDetails(prev => ({ ...prev, [field]: file }));
    }
  };

  const validateStep = () => {
    setError('');

    if (currentStep === 2) {
      if (!workDetails.workCity) {
        setError('Please select a work city');
        return false;
      }
    }

    if (currentStep === 3) {
      if (workDetails.workAreas.length === 0) {
        setError('Please select at least one work area');
        return false;
      }
    }

    if (currentStep === 4) {
      if (!vehicleDetails.vehicleType || !vehicleDetails.vehicleNumber) {
        setError('Please provide vehicle type and number');
        return false;
      }
      // Validate vehicle number format (basic check)
      const vehicleRegex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{1,2}[0-9]{4}$/;
      if (!vehicleRegex.test(vehicleDetails.vehicleNumber.toUpperCase().replace(/\s/g, ''))) {
        setError('Invalid vehicle number format. Example: MH12AB1234');
        return false;
      }
    }

    if (currentStep === 5) {
      if (!idDetails.idType) {
        setError('Please select an ID type');
        return false;
      }

      if (idDetails.idType === 'aadhar') {
        if (!idDetails.aadharNumber || !idDetails.aadharLinkedMobile) {
          setError('Please provide Aadhar number and linked mobile');
          return false;
        }
        // Validate Aadhar format (12 digits)
        if (!/^[0-9]{12}$/.test(idDetails.aadharNumber)) {
          setError('Aadhar number must be 12 digits');
          return false;
        }
        // Validate mobile format
        if (!/^[6-9][0-9]{9}$/.test(idDetails.aadharLinkedMobile)) {
          setError('Invalid mobile number');
          return false;
        }
      } else {
        if (!idDetails.panNumber) {
          setError('Please provide PAN number');
          return false;
        }
        // Validate PAN format
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(idDetails.panNumber.toUpperCase())) {
          setError('Invalid PAN format. Example: ABCDE1234F');
          return false;
        }
      }
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (currentStep === 2) {
      // Just move to next step - will save work details at step 3
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 3) {
      // Save both work city and work areas together
      setLoading(true);
      try {
        await deliveryAPI.updateWorkDetails(workDetails);
        setCurrentStep(currentStep + 1);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to save work details');
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 4) {
      // Save vehicle details
      setLoading(true);
      try {
        await deliveryAPI.updateVehicleDetails(vehicleDetails);
        setCurrentStep(currentStep + 1);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to save vehicle details');
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 5) {
      // Save ID documents
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('idType', idDetails.idType);

        if (idDetails.idType === 'aadhar') {
          formData.append('aadharNumber', idDetails.aadharNumber);
          formData.append('aadharLinkedMobile', idDetails.aadharLinkedMobile);
          if (idDetails.aadharDocument) {
            formData.append('aadharDocument', idDetails.aadharDocument);
          }
        } else {
          formData.append('panNumber', idDetails.panNumber);
          if (idDetails.panDocument) {
            formData.append('panDocument', idDetails.panDocument);
          }
          if (idDetails.addressProofDocument) {
            formData.append('addressProofDocument', idDetails.addressProofDocument);
          }
        }

        await deliveryAPI.submitIdDocuments(formData);
        setCurrentStep(currentStep + 1);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to submit ID documents');
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 6) {
      // Process payment or free submission based on available slots
      if (onboardingSettings.hasFreeSlotsAvailable) {
        await handleFreeSubmission();
      } else {
        await handlePayment();
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Create Cashfree payment order
      const paymentResponse = await paymentAPI.createSubscriptionPayment({
        amount: 500,
        plan: 'delivery_onboarding'
      });

      const { paymentSessionId } = paymentResponse.data.data;

      // Initialize Cashfree
      const cashfree = await load({
        mode: process.env.REACT_APP_CASHFREE_ENV === 'PROD' ? 'production' : 'sandbox'
      });

      // Prepare checkout options
      const checkoutOptions = {
        paymentSessionId: paymentSessionId,
        redirectTarget: '_self'
      };

      // Open Cashfree checkout - it will redirect to payment page
      // After payment, Cashfree will redirect to payment verification page
      cashfree.checkout(checkoutOptions);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handleFreeSubmission = async () => {
    setLoading(true);
    try {
      // Submit directly for approval - backend will check free slots and mark as paid
      const response = await deliveryAPI.submitForApproval();

      if (response.data.success) {
        setSuccess(response.data.message);
        setCurrentStep(7); // Move to success step
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="onboarding-step">
            <div className="welcome-container">
              <h2>Welcome to Foodwinc!</h2>
              <div className="welcome-content">
                <p>Join our platform as a delivery partner and start earning today!</p>
                <div className="benefits-list">
                  <h3>Benefits:</h3>
                  <ul>
                    <li>✓ Flexible working hours</li>
                    <li>✓ Competitive delivery charges</li>
                    <li>✓ Weekly payments</li>
                    <li>✓ Insurance coverage</li>
                    <li>✓ Performance bonuses</li>
                  </ul>
                </div>
                <div className="onboarding-steps-info">
                  <h3>Onboarding Process:</h3>
                  <ol>
                    <li>Select your work city</li>
                    <li>Choose work areas</li>
                    <li>Provide vehicle details</li>
                    <li>Submit ID documents</li>
                    <li>
                      {onboardingSettings.hasFreeSlotsAvailable
                        ? `Complete registration (FREE for first ${onboardingSettings.freeDeliveryPartnerLimit} partners!)`
                        : 'Pay onboarding fee (₹500)'}
                    </li>
                    <li>Wait for admin approval</li>
                  </ol>
                </div>

                {onboardingSettings.hasFreeSlotsAvailable && (
                  <div className="free-slots-banner" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    marginTop: '20px',
                    textAlign: 'center'
                  }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '1.5rem' }}>🎉 Limited Time Offer!</h3>
                    <p style={{ margin: '0 0 5px 0', fontSize: '1.2rem', fontWeight: 'bold' }}>
                      FREE Onboarding for First {onboardingSettings.freeDeliveryPartnerLimit} Partners
                    </p>
                    <p style={{ margin: '0', fontSize: '1rem', opacity: 0.9 }}>
                      Only {onboardingSettings.remainingFreeSlots} slots remaining!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="onboarding-step">
            <h2>Select Work City</h2>
            <p>Choose the city where you want to work:</p>

            <div className="form-group">
              <label>City *</label>
              <select
                value={workDetails.workCity}
                onChange={(e) => handleCityChange(e.target.value)}
                className="form-control"
                required
              >
                <option value="">Select City</option>
                {availableCities.map((city, index) => (
                  <option key={index} value={city.name}>
                    {city.name}, {city.state}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="onboarding-step">
            <h2>Select Work Areas</h2>
            <p>Choose the areas in {workDetails.workCity} where you want to deliver:</p>

            {workAreas.length === 0 ? (
              <div className="no-areas-message">
                <p>No work areas available for this city yet.</p>
                <p>Please contact support or try a different city.</p>
              </div>
            ) : (
              <>
                <div className="surge-info-banner" style={{
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '1rem' }}>
                    💰 Surge Pricing Information
                  </h4>
                  <p style={{ margin: 0, color: '#1e3a8a', fontSize: '0.9rem' }}>
                    Surge multipliers show real-time demand. Higher demand areas offer increased earnings during peak times!
                  </p>
                </div>

                <div className="work-areas-grid">
                  {workAreas.map((area) => {
                    const surge = area.currentSurgeMultiplier || 1.0;
                    const getSurgeColor = (surge) => {
                      if (surge >= 1.4) return '#ef4444';
                      if (surge >= 1.2) return '#f59e0b';
                      if (surge >= 1.1) return '#eab308';
                      return '#10b981';
                    };
                    const getSurgeLabel = (surge) => {
                      if (surge >= 1.4) return 'Peak Demand';
                      if (surge >= 1.2) return 'High Demand';
                      if (surge >= 1.1) return 'Elevated';
                      return 'Normal';
                    };

                    return (
                      <div
                        key={area._id}
                        className={`work-area-card ${workDetails.workAreas.includes(area._id) ? 'selected' : ''}`}
                        onClick={() => handleWorkAreaToggle(area._id)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <h4 style={{ margin: 0 }}>{area.name}</h4>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            background: getSurgeColor(surge),
                            color: 'white',
                            whiteSpace: 'nowrap'
                          }}>
                            {surge.toFixed(1)}x
                          </span>
                        </div>
                        {area.description && <p style={{ margin: '4px 0 8px 0', fontSize: '0.9rem' }}>{area.description}</p>}
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          {getSurgeLabel(surge)}
                        </div>
                        {workDetails.workAreas.includes(area._id) && (
                          <div className="selected-badge">✓ Selected</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="selected-areas-summary">
              <strong>Selected Areas:</strong> {workDetails.workAreas.length > 0 ? workDetails.workAreas.map(id => workAreas.find(a => a._id === id)?.name).filter(Boolean).join(', ') : 'None'}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="onboarding-step">
            <h2>Vehicle Details</h2>
            <p>Provide your vehicle information:</p>

            <div className="form-group">
              <label>Vehicle Type *</label>
              <select
                value={vehicleDetails.vehicleType}
                onChange={(e) => setVehicleDetails({ ...vehicleDetails, vehicleType: e.target.value })}
                className="form-control"
                required
              >
                <option value="">Select Vehicle Type</option>
                <option value="motorcycle">Motorcycle</option>
                <option value="scooty">Scooty</option>
                <option value="ev">Electric Vehicle (EV)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Vehicle Number *</label>
              <input
                type="text"
                value={vehicleDetails.vehicleNumber}
                onChange={(e) => setVehicleDetails({ ...vehicleDetails, vehicleNumber: e.target.value.toUpperCase() })}
                className="form-control"
                placeholder="Example: MH12AB1234"
                required
              />
              <small>Format: State Code + Numbers + Letters + Numbers (e.g., MH12AB1234)</small>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="onboarding-step">
            <h2>Submit ID Documents</h2>
            <p>Choose one option to verify your identity:</p>

            <div className="id-type-selection">
              <div
                className={`id-option ${idDetails.idType === 'aadhar' ? 'selected' : ''}`}
                onClick={() => setIdDetails({ ...idDetails, idType: 'aadhar' })}
              >
                <h3>I have Aadhar</h3>
                <p>Enter Aadhar number linked to your mobile</p>
              </div>

              <div
                className={`id-option ${idDetails.idType === 'pan' ? 'selected' : ''}`}
                onClick={() => setIdDetails({ ...idDetails, idType: 'pan' })}
              >
                <h3>I don't have Aadhar</h3>
                <p>Submit PAN card and address proof</p>
              </div>
            </div>

            {idDetails.idType === 'aadhar' && (
              <div className="id-form">
                <div className="form-group">
                  <label>Aadhar Number *</label>
                  <input
                    type="text"
                    value={idDetails.aadharNumber}
                    onChange={(e) => setIdDetails({ ...idDetails, aadharNumber: e.target.value })}
                    className="form-control"
                    placeholder="Enter 12-digit Aadhar number"
                    maxLength={12}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Aadhar Linked Mobile *</label>
                  <input
                    type="text"
                    value={idDetails.aadharLinkedMobile}
                    onChange={(e) => setIdDetails({ ...idDetails, aadharLinkedMobile: e.target.value })}
                    className="form-control"
                    placeholder="Enter mobile number linked to Aadhar"
                    maxLength={10}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Upload Aadhar Document (Optional)</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'aadharDocument')}
                    className="form-control"
                    accept=".jpg,.jpeg,.png,.pdf"
                  />
                  <small>Accepted formats: JPG, PNG, PDF (Max 10MB)</small>
                </div>
              </div>
            )}

            {idDetails.idType === 'pan' && (
              <div className="id-form">
                <div className="form-group">
                  <label>PAN Number *</label>
                  <input
                    type="text"
                    value={idDetails.panNumber}
                    onChange={(e) => setIdDetails({ ...idDetails, panNumber: e.target.value.toUpperCase() })}
                    className="form-control"
                    placeholder="Example: ABCDE1234F"
                    maxLength={10}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Upload PAN Card *</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'panDocument')}
                    className="form-control"
                    accept=".jpg,.jpeg,.png,.pdf"
                    required
                  />
                  <small>Accepted formats: JPG, PNG, PDF (Max 10MB)</small>
                </div>

                <div className="form-group">
                  <label>Upload Address Proof *</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'addressProofDocument')}
                    className="form-control"
                    accept=".jpg,.jpeg,.png,.pdf"
                    required
                  />
                  <small>Accepted: Driving License, Passport, Electricity Bill, etc. (Max 10MB)</small>
                </div>
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="onboarding-step">
            {onboardingSettings.hasFreeSlotsAvailable ? (
              <>
                <h2>🎉 Congratulations!</h2>
                <div className="payment-info">
                  <div className="free-slots-banner" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '30px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '1.8rem' }}>You're Eligible for FREE Onboarding!</h3>
                    <p style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>
                      You're one of the first {onboardingSettings.freeDeliveryPartnerLimit} delivery partners
                    </p>
                    <p style={{ margin: '0', fontSize: '1.1rem', fontWeight: 'bold', opacity: 0.9 }}>
                      {onboardingSettings.remainingFreeSlots} free slots remaining
                    </p>
                  </div>

                  <div className="fee-breakdown">
                    <h3>What you'll receive (normally ₹500):</h3>
                    <ul>
                      <li>✓ Background verification</li>
                      <li>✓ Document processing</li>
                      <li>✓ Platform onboarding training</li>
                      <li>✓ Delivery bag and branded jacket</li>
                    </ul>
                  </div>

                  <div style={{
                    background: '#f0f9ff',
                    border: '2px solid #3b82f6',
                    borderRadius: '8px',
                    padding: '15px',
                    marginTop: '20px'
                  }}>
                    <p style={{ margin: 0, color: '#1e40af', fontWeight: 500 }}>
                      💡 No payment required! Click "Complete Registration" to submit your application for free.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2>Onboarding Fee</h2>
                <div className="payment-info">
                  <p>A one-time onboarding fee of <strong>₹500</strong> is required to complete your registration.</p>

                  <div className="fee-breakdown">
                    <h3>What this fee covers:</h3>
                    <ul>
                      <li>Background verification</li>
                      <li>Document processing</li>
                      <li>Platform onboarding training</li>
                      <li>Delivery bag and branded jacket</li>
                    </ul>
                  </div>

                  <div className="payment-amount">
                    <h3>Amount to Pay: ₹500</h3>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 7:
        return (
          <div className="onboarding-step">
            <div className="success-container">
              <h2>🎉 Application Submitted!</h2>
              <div className="success-message">
                <p>Your application has been successfully submitted for admin approval.</p>
                <p>You will receive a notification once your application is reviewed.</p>

                <div className="next-steps">
                  <h3>What happens next?</h3>
                  <ol>
                    <li>Our team will verify your documents</li>
                    <li>Background check will be conducted</li>
                    <li>You'll be notified via email/SMS</li>
                    <li>Once approved, you can start delivering!</li>
                  </ol>
                </div>

                <p className="review-time">Typical review time: 2-3 business days</p>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="onboarding-step">
            <div className="status-container">
              {!onboardingStatus || onboardingStatus === 'pending' || onboardingStatus === 'payment_pending' ? (
                <>
                  <h2>⏳ Loading Status...</h2>
                  <p>Please wait while we load your application status.</p>
                </>
              ) : onboardingStatus === 'under_review' ? (
                <>
                  <h2>⏳ Application Under Review</h2>
                  <div className="status-message">
                    <p>Your application is currently being reviewed by our team.</p>
                    <p>We will notify you once the review is complete.</p>

                    <div className="review-info">
                      <h3>Review Process:</h3>
                      <ul>
                        <li>✓ Payment received</li>
                        <li>🔄 Document verification in progress</li>
                        <li>⏳ Background check pending</li>
                        <li>⏳ Final approval pending</li>
                      </ul>
                    </div>

                    <p className="review-time">Typical review time: 2-3 business days</p>
                  </div>
                </>
              ) : onboardingStatus === 'approved' ? (
                <>
                  <h2>✅ Application Approved!</h2>
                  <div className="success-message">
                    <p>Congratulations! Your application has been approved.</p>
                    <p>Redirecting you to your dashboard...</p>

                    <div style={{
                      marginTop: 'var(--space-xl)',
                      marginBottom: 'var(--space-xl)'
                    }}>
                      <div style={{
                        width: '100%',
                        height: '4px',
                        background: 'var(--gray-200)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          background: 'var(--gradient-success)',
                          animation: 'pulse 1s ease-in-out infinite'
                        }}></div>
                      </div>
                    </div>

                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                      If not redirected automatically, <button
                        onClick={() => navigate('/delivery/dashboard')}
                        className="btn btn-primary"
                        style={{ display: 'inline', padding: '4px 12px', fontSize: 'var(--font-size-sm)' }}
                      >
                        click here
                      </button>
                    </p>
                  </div>
                </>
              ) : onboardingStatus === 'rejected' ? (
                <>
                  <h2>❌ Application Rejected</h2>
                  <div className="error-message">
                    <p>Unfortunately, your application was not approved.</p>
                    <p>Please contact support for more information.</p>
                  </div>
                </>
              ) : (
                <>
                  <h2>📋 Application Status</h2>
                  <p>Current Status: {onboardingStatus}</p>
                  <p>Please refresh the page if this persists.</p>
                </>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="delivery-partner-onboarding">
      <div className="onboarding-container">
        {/* Progress bar */}
        {currentStep <= 6 && (
          <div className="progress-bar">
            <div className="progress-steps">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div
                  key={step}
                  className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
                >
                  <div className="step-number">{step}</div>
                  <div className="step-label">
                    {step === 1 && 'Welcome'}
                    {step === 2 && 'City'}
                    {step === 3 && 'Areas'}
                    {step === 4 && 'Vehicle'}
                    {step === 5 && 'ID'}
                    {step === 6 && 'Payment'}
                  </div>
                </div>
              ))}
            </div>
            <div className="progress-line">
              <div
                className="progress-fill"
                style={{ width: `${((currentStep - 1) / 5) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Error and Success Messages */}
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {/* Step Content */}
        <div className="step-content">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        {currentStep !== 7 && (
          <div className="navigation-buttons">
            {currentStep > 1 && currentStep <= 6 && (
              <button
                onClick={handleBack}
                className="btn btn-secondary"
                disabled={loading}
              >
                Back
              </button>
            )}

            {currentStep < 7 && (
              <button
                onClick={handleNext}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading
                  ? 'Please wait...'
                  : currentStep === 6
                    ? (onboardingSettings.hasFreeSlotsAvailable ? 'Complete Registration (FREE)' : 'Pay ₹500')
                    : currentStep === 1
                      ? "Let's Start"
                      : 'Next'}
              </button>
            )}
          </div>
        )}

        {currentStep === 7 && (
          <div className="final-actions">
            <button
              onClick={() => navigate('/delivery/dashboard')}
              className="btn btn-primary"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryPartnerOnboarding;
